/**
 * Profile Guard
 *
 * Protects the Notebook, Administration, Academy and licence routes with a
 * COMPLETE profile for the active team:
 * 1. They must be a member of the active team (have a profile). If not, they
 *    go to /my-blume (Blume staff on a partner-scoped route are deferred to
 *    the partner guards, which handle staff access).
 * 2. Their profile must be complete - a job title, a school when the district
 *    lists schools, and their own confirmation (see
 *    store/account/profile-completeness.ts). If not, they go to
 *    /complete-profile, which returns them here once saved. Blume staff
 *    visiting a district are exempt.
 *
 * Runs after `dataOptInGuard` (terms first, then the profile). Both redirects
 * are returned as UrlTrees, never navigated to here, so Angular's ordered
 * guard results pick the redirect deterministically.
 *
 * @example
 * {
 *   path: 'notebook/:partnerId',
 *   canActivate: [authGuard, emailVerifiedGuard, dataOptInGuard, profileGuard, partnerMemberGuard],
 *   loadComponent: () => import('./features/notebook/notebook.page'),
 * }
 */

import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AccountState } from 'src/app/store/account/account.state';

/** See the file header: membership of the active team, then a complete profile. */
export const profileGuard: CanActivateFn = (route, state) => {
  const accountState = inject(AccountState);
  const router = inject(Router);

  const requestedRoute = state.url;

  // Get active team and profile from signal-based AccountState
  const activeProfile = accountState.activeProfile();

  // Check if user has a profile for the active team
  if (!activeProfile) {
    // Blume staff entering a district they aren't a member of won't have a
    // real profile yet, and the active team may not be reconciled to the URL
    // on this first pass. Defer to the partner-scoped guard
    // (partnerMemberGuard / partnerAdminGuard), which authorizes staff access
    // and reconciles via enterDistrictAsStaff. Only defer when the route is
    // partner-scoped so non-partner routes keep the strict profile check.
    const partnerId = route.paramMap.get('partnerId');
    if (partnerId && accountState.isDistrictStaff()) {
      return true;
    }
    return router.createUrlTree(['/my-blume']);
  }

  // A member whose profile is incomplete finishes it first, then returns here.
  if (accountState.activeProfileMissing().length > 0) {
    return router.createUrlTree(['/complete-profile'], { queryParams: { returnUrl: requestedRoute } });
  }

  return true;
};
